import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Save, User as UserIcon, ShieldCheck, Bell, Globe, KeyRound,
  RefreshCw, AlertTriangle, Copy, Loader2, Trash2, QrCode, Smartphone,
} from "lucide-react";
import FactoryResetDialog from "@/components/admin/FactoryResetDialog";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    company_name: user?.company_name || "",
    phone: user?.phone || "",
  });
  const [notif, setNotif] = useState({
    email_invoices: true,
    email_overdue: true,
    email_security: true,
    email_weekly_digest: false,
  });
  const [lang, setLang] = useState("fi");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [twofaStatus, setTwofaStatus] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrPw, setQrPw] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrError, setQrError] = useState("");

  useEffect(() => {
    api.get("/auth/2fa/status").then(r => setTwofaStatus(r.data)).catch(() => {});
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/settings/profile", form);
      await refresh();
      toast.success("Profiili tallennettu");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const savePassword = async () => {
    if (pw.next.length < 6) return toast.error("Vähintään 6 merkkiä");
    if (pw.next !== pw.confirm) return toast.error("Salasanat eivät täsmää");
    setSaving(true);
    try {
      await api.put("/settings/password", { current_password: pw.current, new_password: pw.next });
      toast.success("Salasana päivitetty");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const regenRecovery = async () => {
    if (!window.confirm("Vanhat varakoodit lakkaavat toimimasta. Jatketaanko?")) return;
    try {
      const r = await api.post("/auth/2fa/regenerate-recovery");
      setRecovery(r.data.recovery_codes);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openQrDialog = () => {
    setQrPw("");
    setQrData(null);
    setQrError("");
    setQrOpen(true);
  };

  const fetchQr = async () => {
    if (!qrPw) return setQrError("Syötä nykyinen salasana");
    setQrLoading(true);
    setQrError("");
    try {
      const r = await api.post("/auth/2fa/show-qr", { password: qrPw });
      setQrData(r.data);
    } catch (e) {
      setQrError(formatApiError(e));
    }
    setQrLoading(false);
  };

  const closeQrDialog = (v) => {
    if (!v) {
      setQrOpen(false);
      setQrPw("");
      setQrData(null);
      setQrError("");
    }
  };

  const TAB_BTN = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`settings-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl transition ${
        tab === id ? "bg-white/8 text-white" : "text-white/50 hover:text-white hover:bg-white/5"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Tili</p>
        <h1 className="text-3xl font-display gradient-text-luxe">Asetukset</h1>
        <p className="text-white/60 text-sm mt-1">Profiili, salasana, 2FA, ilmoitukset, kieli ja muut.</p>
      </header>

      <div className="flex gap-2 flex-wrap">
        <TAB_BTN id="profile" icon={UserIcon} label="Profiili" />
        <TAB_BTN id="password" icon={KeyRound} label="Salasana" />
        <TAB_BTN id="twofa" icon={ShieldCheck} label="2FA" />
        <TAB_BTN id="notifications" icon={Bell} label="Ilmoitukset" />
        <TAB_BTN id="locale" icon={Globe} label="Kieli & alue" />
        {user?.role === "admin" && (
          <TAB_BTN id="danger" icon={AlertTriangle} label="Vaaravyöhyke" />
        )}
      </div>

      {tab === "profile" && (
        <div className="glass-card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 relative z-10">
            <div><Label>Koko nimi</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-name" /></div>
            <div><Label>Yritys</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="profile-company" /></div>
            <div><Label>Puhelin</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
            <div><Label>Sähköposti</Label><Input value={user?.email || ""} disabled className="opacity-60" /></div>
            <div><Label>Rooli</Label><Input value={user?.role || ""} disabled className="opacity-60" /></div>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="btn-luxe inline-flex items-center gap-2" data-testid="save-profile">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Tallenna
          </Button>
        </div>
      )}

      {tab === "password" && (
        <div className="glass-card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative z-10">
            <div><Label>Nykyinen salasana</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
            <div><Label>Uusi salasana</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-next" /></div>
            <div><Label>Vahvista uusi</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <Button onClick={savePassword} disabled={saving} className="btn-luxe inline-flex items-center gap-2" data-testid="save-password">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Vaihda salasana
          </Button>
        </div>
      )}

      {tab === "twofa" && (
        <div className="space-y-4">
          {/* QR-koodi heti alussa: lisää tili Authenticatoriin */}
          {twofaStatus?.enabled && (
            <div className="glass-card p-6 space-y-4">
              <div className="relative z-10 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Authenticator-sovellus</p>
                  <h2 className="text-lg font-display text-white">Lisää tili Authenticatoriin</h2>
                  <p className="text-xs text-white/55 mt-1 leading-relaxed">
                    Avaa QR-koodi ja skannaa se uudella puhelimella tai toisessa Authenticator-sovelluksessa.
                    Vanhat merkinnät jatkavat toimintaansa — sama 6-numeroinen koodi näkyy kaikilla laitteilla.
                  </p>
                </div>
                <Button
                  onClick={openQrDialog}
                  className="btn-luxe inline-flex items-center gap-2 flex-shrink-0"
                  data-testid="twofa-show-qr"
                >
                  <Smartphone className="w-4 h-4" /> Näytä QR-koodi
                </Button>
              </div>
            </div>
          )}

          {/* Status + recovery */}
          <div className="glass-card p-6 space-y-4">
            <div className="relative z-10 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${twofaStatus?.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-display text-white">{twofaStatus?.enabled ? "2FA aktiivinen" : "2FA ei aktivoitu"}</p>
                <p className="text-xs text-white/50">
                  {twofaStatus?.enabled
                    ? `Aktivoitu ${twofaStatus.enrolled_at?.slice(0, 10) || ""}. Varakoodeja jäljellä: ${twofaStatus.recovery_codes_remaining}.`
                    : "2FA aktivoidaan seuraavalla kirjautumisella."}
                </p>
              </div>
              {twofaStatus?.enabled && (
                <Button onClick={regenRecovery} variant="outline" className="border-amber-400/30 inline-flex items-center gap-2" data-testid="regen-recovery">
                  <RefreshCw className="w-4 h-4" /> Uudet varakoodit
                </Button>
              )}
            </div>
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 flex gap-2 relative z-10">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
              <p>2FA pakollinen kaikille käyttäjille. Jos hukkasit Authenticatorin, kirjaudu ulos ja klikkaa &quot;Unohditko Authenticatorin?&quot; sisäänkirjautumisen 2FA-vaiheessa.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="glass-card p-6 space-y-3 relative z-10">
          {[
            ["email_invoices", "Sähköposti uudesta laskusta", "Lähetä ilmoitus aina kun lasku luodaan"],
            ["email_overdue", "Sähköposti erääntyneestä laskusta", "Päivittäinen muistutus erääntyneistä"],
            ["email_security", "Tietoturvailmoitukset", "Epäilyttävät kirjautumiset ja hälytykset"],
            ["email_weekly_digest", "Viikkoraportti", "Yhteenveto viikon liikevaihdosta sähköpostiin"],
          ].map(([k, t, d]) => (
            <div key={k} className="flex items-start justify-between p-3 rounded-lg border border-white/5 bg-white/[0.02]">
              <div>
                <p className="text-sm text-white">{t}</p>
                <p className="text-xs text-white/40">{d}</p>
              </div>
              <Switch checked={notif[k]} onCheckedChange={(v) => setNotif({ ...notif, [k]: v })} data-testid={`notif-${k}`} />
            </div>
          ))}
          <Button className="btn-luxe inline-flex items-center gap-2" onClick={() => toast.success("Ilmoitusasetukset tallennettu")}>
            <Save className="w-4 h-4" /> Tallenna
          </Button>
        </div>
      )}

      {tab === "locale" && (
        <div className="glass-card p-6 space-y-4 relative z-10">
          <div>
            <Label>Sovelluksen kieli</Label>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="locale-language">
              <option value="fi">Suomi (FI)</option>
              <option value="en">English (EN)</option>
              <option value="sv">Svenska (SV)</option>
            </select>
          </div>
          <div>
            <Label>Aikavyöhyke</Label>
            <select className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" defaultValue="Europe/Helsinki">
              <option value="Europe/Helsinki">Europe/Helsinki (UTC+2 / +3)</option>
              <option value="Europe/Stockholm">Europe/Stockholm</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <div>
            <Label>Valuutta</Label>
            <select className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" defaultValue="EUR">
              <option value="EUR">EUR – Euro</option>
              <option value="USD">USD – US Dollar</option>
              <option value="SEK">SEK – Ruotsin kruunu</option>
            </select>
          </div>
          <Button className="btn-luxe inline-flex items-center gap-2" onClick={() => toast.success("Asetukset tallennettu")}>
            <Save className="w-4 h-4" /> Tallenna
          </Button>
        </div>
      )}

      {tab === "danger" && user?.role === "admin" && (
        <div className="space-y-4 max-w-2xl">
          <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.03] p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400 mt-0.5" />
              <div>
                <h2 className="text-xl font-display text-red-200">Globaali tehdaspalautus</h2>
                <p className="text-sm text-white/60 mt-1">
                  Tämä toiminto poistaa <strong className="text-red-300">kaiken liiketoimintadatan</strong>, kaikki
                  muut käyttäjät (henkilökunta, asiakkaat, muut adminit) ja lisenssit. <br />
                  <strong className="text-emerald-300">Säilytetään:</strong> oma admin-tunnuksesi sekä kaikki sovelluksen työkalut ja toiminnot.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setResetOpen(true)}
              className="border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-2"
              data-testid="settings-global-reset-button"
            >
              <Trash2 className="w-4 h-4" /> Tehdaspalauta koko Apps2Go
            </Button>
          </div>

          <div className="text-xs text-white/40 leading-relaxed">
            <p><strong>Vinkki:</strong> Voit myös palauttaa yksittäisen moduulin sen omalta sivulta — klikkaa header-rivin
              hammasrattaan kuvaketta. Tämä on hyödyllinen kun haluat tyhjentää esim. vain laskut tai vain kuitit
              ilman, että muut moduulit muuttuvat.</p>
          </div>
        </div>
      )}

      <FactoryResetDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        scope="global"
        onComplete={() => {
          setTimeout(() => window.location.href = "/", 1200);
        }}
      />

      {/* Recovery code dialog */}
      <Dialog open={!!recovery} onOpenChange={(v) => !v && setRecovery(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Uudet varakoodit</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 flex gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <p>Tallenna nämä turvalliseen paikkaan – näytetään vain kerran.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-black/40 p-4 rounded-lg">
            {(recovery || []).map((c) => <div key={c} className="text-amber-300">{c}</div>)}
          </div>
          <Button onClick={() => { navigator.clipboard.writeText((recovery || []).join("\n")); toast.success("Kopioitu"); }} className="btn-luxe inline-flex items-center gap-2">
            <Copy className="w-4 h-4" /> Kopioi
          </Button>
        </DialogContent>
      </Dialog>

      {/* QR-koodin näyttö (salasanavahvistus) */}
      <Dialog open={qrOpen} onOpenChange={closeQrDialog}>
        <DialogContent className="max-w-md" data-testid="twofa-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-amber-300" />
              {qrData ? "Skannaa QR-koodi" : "Vahvista salasanalla"}
            </DialogTitle>
          </DialogHeader>

          {!qrData ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
                <p>
                  Turvallisuussyistä QR-koodi näytetään vasta kun olet vahvistanut salasanasi.
                </p>
              </div>
              <div>
                <Label>Nykyinen salasana</Label>
                <Input
                  type="password"
                  value={qrPw}
                  onChange={(e) => setQrPw(e.target.value)}
                  placeholder="•••••••"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && fetchQr()}
                  data-testid="twofa-qr-password"
                />
              </div>
              {qrError && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200">
                  {qrError}
                </div>
              )}
              <Button
                onClick={fetchQr}
                disabled={qrLoading}
                className="btn-luxe w-full inline-flex items-center justify-center gap-2"
                data-testid="twofa-qr-confirm"
              >
                {qrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                Näytä QR-koodi
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 mx-auto w-fit shadow-2xl shadow-amber-500/20">
                <img
                  src={qrData.qr_data_url}
                  alt="2FA QR-koodi"
                  className="w-56 h-56 block"
                  data-testid="twofa-qr-image"
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tai syötä avain käsin</p>
                <div className="bg-black/40 rounded-lg px-3 py-3 border border-white/5">
                  <p
                    className="font-mono text-sm tracking-wider text-amber-200"
                    style={{ fontVariantNumeric: "slashed-zero tabular-nums" }}
                    data-testid="twofa-qr-secret"
                  >
                    {(qrData.secret.match(/.{1,4}/g) || []).join(" ")}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(qrData.secret);
                    toast.success("Avain kopioitu");
                  }}
                  className="text-xs gap-1 text-white/60 hover:text-white mt-2"
                >
                  <Copy className="w-3 h-3" /> Kopioi avain
                </Button>
              </div>
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-100 leading-relaxed">
                Skannaa QR-koodi Google Authenticator-, Authy- tai vastaavalla sovelluksella.
                Vanhat merkinnät jatkavat toimintaansa — kaikki laitteet näyttävät saman 6-numeroisen koodin.
              </div>
              <Button
                variant="outline"
                onClick={() => closeQrDialog(false)}
                className="w-full border-white/10 text-white hover:bg-white/5"
                data-testid="twofa-qr-close"
              >
                Valmis
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
